import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { WorkflowStepBatchConfigSchema } from "../../types/batch";

const kind = z.enum(["minimum_order_value", "prepaid_freight"]);
export const supplierWarehouseConstraintInputSchema = z.object({
  dataSourceLink: z.object({ id: z.string().uuid() }),
  metadataId: z.string().min(1),
  changeOperation: z.enum(["insert", "update", "delete"]),
  changeLsn: z.string().regex(/^\d+$/),
  supplierCode: z.string().nullable(),
  warehouseCode: z.string().nullable(),
  constraintKind: z.string().nullable(),
  amount: z.union([z.string(), z.number()]).nullable(),
  productGroup: z.string().nullable().optional(),
  sourceType: z.string().nullable().optional(),
  sourceIntegrity: z.string().nullable().optional(),
  activeFrom: z.iso.date().nullable().optional(),
  activeTo: z.iso.date().nullable().optional(),
});
export type SupplierWarehouseConstraintInput = z.infer<
  typeof supplierWarehouseConstraintInputSchema
>;

export const upsertSupplierWarehouseConstraintStep = defineStepConfig({
  type: "upsert_supplier_warehouse_constraint",
  name: "Upsert Supplier Warehouse Constraint",
  description:
    "Maintains source-specific MOV and prepaid freight constraints, including CDC deletion and ordering.",
  category: "Transformation",
  libraryModule: "Procurement",
  inputSchema: supplierWarehouseConstraintInputSchema,
  outputSchema: z.object({
    written: z.boolean(),
    deleted: z.boolean(),
    skipped: z.boolean(),
    reason: z
      .enum([
        "unresolved_supplier",
        "unresolved_warehouse",
        "unsupported_kind",
        "stale_event",
      ])
      .nullable(),
  }),
  configSchema: z.object({
    batch: WorkflowStepBatchConfigSchema.prefault({ size: 500 }),
    originSystem: z
      .string()
      .optional()
      .describe("Warehouse lookup origin system."),
    supplierOriginSystem: z.string().optional(),
    writeOriginSystem: z.string().min(1),
    kindMapping: z
      .record(z.string(), kind)
      .prefault({
        minimum_order_value: "minimum_order_value",
        prepaid_freight: "prepaid_freight",
      })
      .describe(
        "Source values mapped to generic constraint kinds. A `<kind>:<integrity>` key (e.g. `1:2`) beats a bare `<kind>` key, for sources that split one kind across integrity slots. Unmapped rows are retired.",
      ),
    supplierCodePrefixAliases: z
      .record(z.string().min(1), z.string())
      .optional()
      .describe(
        "Supplier-code prefixes rewritten before lookup, for sources that spell a division differently from the supplier master (e.g. `W+` -> `WG`).",
      ),
  }),
  emits: [] as const,
  execution: {
    startToCloseTimeout: "30 minutes",
    retry: { maximumAttempts: 2 },
  },
});
